import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Undo2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

export default function WithdrawalPage() {
  const [form, setForm] = useState({ name: "", order_number: "", email: "", reason: "" });
  const [step, setStep] = useState("form");
  const [sending, setSending] = useState(false);

  const valid = form.name.trim() && form.order_number.trim() && form.email.includes("@");

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/withdrawals", form);
      setStep("done");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err, "Declarația nu a putut fi trimisă. Încearcă din nou."));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6" data-testid="withdrawal-page">
      <p className="text-xs font-bold uppercase tracking-[0.08em] text-navy">OUG nr. 34/2014 · art. 11¹</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
        Retrage-te din contract aici
      </h1>
      <p className="mt-4 text-base leading-relaxed text-ink">
        Ai 14 zile calendaristice de la primirea produselor să te retragi din contract, fără motiv și fără penalități.
        Completezi declarația online, o confirmi, iar noi îți trimitem imediat pe email confirmarea de primire, cu data și ora transmiterii.
      </p>

      {step === "done" ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-10 rounded-lg border border-line bg-white p-10 text-center"
          data-testid="withdrawal-success"
        >
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-skytint">
            <CheckCircle2 className="h-8 w-8 text-navy" />
          </span>
          <h2 className="mt-5 font-display text-2xl font-bold text-navy">Declarația de retragere a fost înregistrată</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted2">
            Ți-am trimis pe email confirmarea de primire, cu conținutul declarației, data și ora transmiterii.
            Te contactăm pentru detaliile returului, iar rambursarea se face în maximum 14 zile de la primirea produselor.
          </p>
          <Link to="/" className="mt-7 inline-flex h-12 items-center rounded-lg bg-navy px-8 text-base font-bold text-white hover:bg-navy-dark" data-testid="withdrawal-home-link">
            Înapoi la pagina principală
          </Link>
        </motion.div>
      ) : (
        <div className="mt-10 rounded-lg border border-line bg-[#F4F6F8] p-7 sm:p-9" data-testid="withdrawal-form">
          <div className="grid gap-5">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Numele tău complet</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                placeholder="Ex: Andrei Popescu"
                data-testid="withdrawal-name-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Numărul comenzii</label>
              <input
                value={form.order_number}
                onChange={(e) => setForm({ ...form, order_number: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                placeholder="Ex: DZ-1001"
                data-testid="withdrawal-order-input"
              />
              <p className="mt-1.5 text-xs text-muted2">Îl găsești în emailul de confirmare a comenzii sau în contul tău.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Email pentru confirmare</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                placeholder="email@exemplu.ro"
                data-testid="withdrawal-email-input"
              />
              <p className="mt-1.5 text-xs text-muted2">Pe această adresă primești confirmarea de primire a retragerii.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Motiv (opțional)</label>
              <textarea
                rows={3}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="w-full resize-none rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                placeholder="Nu este obligatoriu — ne ajută să ne îmbunătățim"
                data-testid="withdrawal-reason-input"
              />
            </div>
          </div>

          {step === "form" ? (
            <button
              onClick={() => {
                if (!valid) {
                  toast.error("Completează numele, numărul comenzii și un email valid");
                  return;
                }
                setStep("confirm");
                window.scrollTo(0, 0);
              }}
              className="mt-7 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy text-base font-bold text-white transition-colors hover:bg-navy-dark"
              data-testid="withdrawal-review-button"
            >
              <Undo2 className="h-4 w-4" /> Trimite declarația de retragere
            </button>
          ) : (
            <div className="mt-7 rounded-lg border-2 border-navy bg-white p-6" data-testid="withdrawal-confirm-box">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-navy">Verifică și confirmă</p>
              <p className="mt-3 text-sm text-ink">
                Eu, <strong>{form.name}</strong>, declar că mă retrag din contractul aferent comenzii{" "}
                <strong>{form.order_number}</strong>, încheiat cu Detergent Studio SRL.
              </p>
              <p className="mt-2 text-sm text-ink">
                Confirmarea de primire va fi trimisă la <strong>{form.email}</strong>.
              </p>
              {form.reason && <p className="mt-2 text-sm italic text-muted2">Motiv: {form.reason}</p>}
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={submit}
                  disabled={sending}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-navy text-base font-bold text-white transition-colors hover:bg-navy-dark disabled:opacity-60"
                  data-testid="withdrawal-confirm-button"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {sending ? "Se trimite..." : "Confirmați retragerea"}
                </button>
                <button
                  onClick={() => setStep("form")}
                  className="flex h-12 items-center justify-center rounded-lg border-[1.5px] border-navy px-6 text-sm font-bold text-navy transition-colors hover:bg-skytint"
                  data-testid="withdrawal-back-button"
                >
                  Modifică datele
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-center text-xs text-muted2">
        Mai multe detalii în <Link to="/legal#retur" className="font-bold text-navy underline">Politica de retur</Link>.
      </p>
    </main>
  );
}
